import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ZodType, ZodTypeDef } from 'zod'

export type SyncScope = 'url' | 'session' | 'persistent'

const STORAGE_PREFIX = 'mmm-v3:syn:'

function readStorage<T>(
  scope: 'session' | 'persistent',
  key: string,
  schema: ZodType<T, ZodTypeDef, unknown>,
  fallback: T,
): T {
  try {
    const store = scope === 'session' ? sessionStorage : localStorage
    const raw = store.getItem(`${STORAGE_PREFIX}${key}`)
    if (raw === null) return fallback
    const parsed = schema.safeParse(JSON.parse(raw))
    if (!parsed.success) {
      console.warn(`[useSyncedState] invalid stored value for ${key}, using default`, parsed.error)
      return fallback
    }
    return parsed.data
  } catch {
    return fallback
  }
}

function writeStorage(scope: 'session' | 'persistent', key: string, value: unknown) {
  const store = scope === 'session' ? sessionStorage : localStorage
  store.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value))
}

/**
 * Sync state to URL search-param, sessionStorage, or localStorage.
 *
 * The `scope` argument MUST be a literal const at each call site — the
 * useState mirror for non-URL scopes is initialised lazily on first mount
 * via `readStorage(scope, ...)`, so a runtime-changing scope (e.g.
 * `'url' → 'session'`) would silently keep stale state without re-reading
 * storage. Today every consumer passes a literal; this constraint locks
 * that contract in.
 */
export function useSyncedState<T>(
  key: string,
  schema: ZodType<T, ZodTypeDef, unknown>,
  scope: SyncScope,
  defaultValue: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  // Non-URL scopes: mirror into local state so setter calls trigger re-renders.
  // useState is initialised once from storage; the setter keeps both in sync.
  // This is intentionally in-tab only — no cross-tab consistency is required.
  const [local, setLocal] = useState<T>(() =>
    scope !== 'url'
      ? readStorage(scope as 'session' | 'persistent', key, schema, defaultValue)
      : defaultValue,
  )

  const value = useMemo<T>(() => {
    if (scope === 'url') {
      const raw = searchParams.get(key)
      if (raw === null) return defaultValue
      const parsed = schema.safeParse(raw)
      if (!parsed.success) {
        console.warn(`[useSyncedState] invalid URL param for ${key}, using default`, parsed.error)
        return defaultValue
      }
      return parsed.data
    }
    // Non-URL: return local React state (kept in sync with storage by the setter below).
    return local
  }, [key, schema, scope, defaultValue, searchParams, local])

  const setter = useCallback(
    (next: T) => {
      if (scope === 'url') {
        // Functional updater form: reads react-router's internal params ref at call
        // time rather than the closure snapshot. This gives closure hygiene (the
        // setter is safe to call from stale closures) but does NOT compose
        // back-to-back single-key calls in the same synchronous handler — react-router
        // updates searchParamsRef in a post-commit useEffect, so the second call's
        // `prev` is still the pre-render state. Use useSetMultipleSearchParams when
        // you need to write 2+ URL keys atomically in one event handler.
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev)
            params.set(key, String(next))
            return params
          },
          { replace: false },
        )
      } else {
        setLocal(next)
        writeStorage(scope, key, next)
      }
    },
    [key, scope, setSearchParams],
  )

  return [value, setter]
}
